import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { FileBadge, BadgeCheck, Send, Clock, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PURPOSES = ['Bank', 'Embassy / Visa', 'New Employer', 'Personal Records', 'Other'];

const REQ_TYPES = [
  { key: 'salary_certificate', label: 'Salary Certificate', desc: 'Confirms your salary for banks, embassies, etc.', icon: FileBadge, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { key: 'experience_certificate', label: 'Experience Certificate', desc: 'Confirms your role and tenure with the company.', icon: BadgeCheck, color: 'text-sky-600', bg: 'bg-sky-50' },
  { key: 'noc_certificate', label: 'No Objection Certificate (NOC)', desc: 'For travel/visa or work — states the company has no objection.', icon: FileBadge, color: 'text-teal-600', bg: 'bg-teal-50' },
];

const STATUS_META = {
  pending: { label: 'Pending with HR', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  issued: { label: 'Issued', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Declined', cls: 'bg-rose-100 text-rose-700', icon: XCircle },
};

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const PortalRequestDocs = () => {
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [dialogType, setDialogType] = useState(null); // { key, label }
  const [purpose, setPurpose] = useState('');
  const [customPurpose, setCustomPurpose] = useState('');
  const [addressedTo, setAddressedTo] = useState('');
  const [noc, setNoc] = useState({ purpose_type: 'travel', country: '', travel_from: '', travel_to: '', embassy: '', purpose: '' });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/employee-portal/document-requests`, { headers });
      setRequests(r.data?.requests || []);
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = (rt) => {
    setDialogType(rt);
    setPurpose('');
    setCustomPurpose('');
    setAddressedTo('');
    setNoc({ purpose_type: 'travel', country: '', travel_from: '', travel_to: '', embassy: '', purpose: '' });
  };

  const isNoc = dialogType?.key === 'noc_certificate';

  const nocValid = () => {
    if (!isNoc) return true;
    if (noc.purpose_type === 'travel') return noc.country.trim() && noc.travel_from && noc.travel_to && noc.embassy.trim();
    return addressedTo.trim() && noc.purpose.trim();
  };

  const submitRequest = async () => {
    if (!dialogType) return;
    setSubmitting(dialogType.key);
    try {
      if (isNoc) {
        await axios.post(`${API}/api/employee-portal/document-requests`, {
          request_type: 'noc_certificate',
          note: noc.purpose_type === 'travel' ? `Travel to ${noc.country}` : noc.purpose,
          addressed_to: noc.purpose_type === 'travel' ? noc.embassy : addressedTo.trim(),
          noc_purpose_type: noc.purpose_type,
          country: noc.country.trim(),
          travel_from: noc.travel_from,
          travel_to: noc.travel_to,
          embassy: noc.embassy.trim(),
          purpose: noc.purpose.trim(),
        }, { headers });
      } else {
        const note = purpose === 'Other' ? customPurpose.trim() : purpose;
        await axios.post(`${API}/api/employee-portal/document-requests`,
          { request_type: dialogType.key, note, addressed_to: addressedTo.trim() }, { headers });
      }
      toast({ title: 'Request sent', description: `Your ${dialogType.label} request has been sent to HR.` });
      setDialogType(null);
      load();
    } catch (e) {
      toast({
        title: 'Could not request',
        description: e?.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally { setSubmitting(null); }
  };

  // Salary certificates are usually addressed to a specific party.
  const needsAddressee = dialogType?.key === 'salary_certificate';
  const addresseePlaceholder = purpose === 'Bank' ? 'e.g. Emirates NBD'
    : purpose === 'Embassy / Visa' ? 'e.g. Embassy of India, Abu Dhabi'
    : purpose === 'New Employer' ? 'e.g. ABC Trading LLC'
    : 'e.g. bank / embassy / company name';

  const pendingTypes = new Set(requests.filter(r => r.status === 'pending').map(r => r.request_type));

  return (
    <Card className="border-0 shadow-sm" data-testid="portal-request-docs">
      <CardContent className="p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Request a Document</h3>
        <div className="grid grid-cols-1 gap-2">
          {REQ_TYPES.map(rt => {
            const Icon = rt.icon;
            const isPending = pendingTypes.has(rt.key);
            return (
              <div key={rt.key} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100">
                <div className={`h-10 w-10 rounded-lg ${rt.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`h-5 w-5 ${rt.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{rt.label}</p>
                  <p className="text-[11px] text-gray-400 leading-snug">{rt.desc}</p>
                </div>
                <Button
                  size="sm"
                  variant={isPending ? 'outline' : 'default'}
                  disabled={isPending || submitting === rt.key}
                  onClick={() => openDialog(rt)}
                  data-testid={`request-${rt.key}`}
                >
                  {isPending ? 'Requested' : (<><Send className="h-3.5 w-3.5 mr-1" /> Request</>)}
                </Button>
              </div>
            );
          })}
        </div>

        {requests.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">My Requests</p>
            <div className="space-y-2">
              {requests.map(r => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                const SIcon = meta.icon;
                return (
                  <div key={r.id} className="flex items-center justify-between gap-2" data-testid={`my-request-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{r.request_label}</p>
                      <p className="text-[10px] text-gray-400">
                        Requested {fmtDate(r.created_at)}{r.note ? ` · for ${r.note}` : ''}{r.addressed_to ? ` · to ${r.addressed_to}` : ''}
                      </p>
                    </div>
                    <Badge className={`text-[10px] ${meta.cls} flex items-center gap-1`}>
                      <SIcon className="h-3 w-3" /> {meta.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={!!dialogType} onOpenChange={(o) => !o && setDialogType(null)}>
        <DialogContent className="max-w-sm" data-testid="request-purpose-dialog">
          <DialogHeader>
            <DialogTitle>Request {dialogType?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {isNoc ? (
              <>
                <p className="text-xs text-gray-500">Is this NOC for travel or for work / other purposes?</p>
                <div className="flex gap-2">
                  {[['travel', 'Travelling'], ['work_other', 'Work / Other']].map(([v, lbl]) => (
                    <button key={v} type="button" onClick={() => setNoc(n => ({ ...n, purpose_type: v }))}
                      className={`flex-1 px-3 py-1.5 rounded-full text-xs font-medium border ${noc.purpose_type === v ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}
                      data-testid={`noc-req-type-${v}`}>{lbl}</button>
                  ))}
                </div>
                {noc.purpose_type === 'travel' ? (
                  <>
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Country to visit *</p>
                      <Input value={noc.country} onChange={e => setNoc(n => ({ ...n, country: e.target.value }))} placeholder="e.g. Netherlands" data-testid="noc-req-country" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">From *</p>
                        <Input type="date" value={noc.travel_from} onChange={e => setNoc(n => ({ ...n, travel_from: e.target.value }))} data-testid="noc-req-from" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Till *</p>
                        <Input type="date" value={noc.travel_to} onChange={e => setNoc(n => ({ ...n, travel_to: e.target.value }))} data-testid="noc-req-to" />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Which embassy to address? *</p>
                      <Input value={noc.embassy} onChange={e => setNoc(n => ({ ...n, embassy: e.target.value }))} placeholder="e.g. Embassy of Netherlands, Abu Dhabi" data-testid="noc-req-embassy" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Whom to address the NOC? *</p>
                      <Input value={addressedTo} onChange={e => setAddressedTo(e.target.value)} placeholder="e.g. Emirates NBD / To Whom It May Concern" data-testid="noc-req-addressee" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Purpose of the NOC *</p>
                      <Input value={noc.purpose} onChange={e => setNoc(n => ({ ...n, purpose: e.target.value }))} placeholder="e.g. opening a personal bank account" data-testid="noc-req-purpose" />
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500">What do you need it for? This helps HR prepare the right document.</p>
                <div className="flex flex-wrap gap-2">
                  {PURPOSES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPurpose(p)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        purpose === p ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'
                      }`}
                      data-testid={`purpose-${p.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                {purpose === 'Other' && (
                  <Input
                    autoFocus
                    placeholder="Type the purpose…"
                    value={customPurpose}
                    onChange={(e) => setCustomPurpose(e.target.value)}
                    maxLength={80}
                    data-testid="purpose-custom-input"
                  />
                )}
                {needsAddressee && purpose && (
                  <div className="pt-1">
                    <p className="text-xs font-medium text-gray-700 mb-1">Whom should it be addressed to?</p>
                    <Input
                      placeholder={addresseePlaceholder}
                      value={addressedTo}
                      onChange={(e) => setAddressedTo(e.target.value)}
                      maxLength={120}
                      data-testid="addressed-to-input"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Enter the bank, embassy or company name. Leave blank for “To Whom It May Concern”.</p>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogType(null)}>Cancel</Button>
            <Button
              onClick={submitRequest}
              disabled={submitting === dialogType?.key || (isNoc ? !nocValid() : (!purpose || (purpose === 'Other' && !customPurpose.trim())))}
              data-testid="submit-request-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1" /> Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PortalRequestDocs;
